// Render plugin for `table_rendered` SSE events.
// Registered in AgentChatPageV2 config so the agentkit core stays generic.
// Each event carries { id, platinumData, title, toolCallId, customer, job, spec }.

import React from 'react'
import type { RenderPlugin, PluginAgentSSEEvent } from '../agent'
import { pluginEventId } from '../agent'
import InlinePlatinumTable from '../components/agent/InlinePlatinumTable'

interface TableEntry {
  id: string
  platinumData: unknown
  title: string
  customer?: string
  job?: string
  spec?: string
}

// Plugin state: map from table id → table data, all belonging to one toolCallId bucket.
type TablePluginState = Map<string, TableEntry>

export const carbonTablePlugin: RenderPlugin<TablePluginState> = {
  eventTypes: ['table_rendered'],

  init(): TablePluginState {
    return new Map()
  },

  reduce(state: TablePluginState, event: PluginAgentSSEEvent): TablePluginState {
    const d = event.data
    // The sandbox `table_rendered` marker event carries `toolCallId` but not
    // always an explicit `id` (see sandbox-image/plugins/render_table.ts). A
    // batch render_tables call emits several such events sharing one toolCallId,
    // so a bare toolCallId fallback would overwrite each table with the next and
    // only the last would display. Suffix with the fold position instead — the
    // fold over pluginEvents is deterministic, so replay derives the same keys.
    const id = pluginEventId(d, state.size)
    if (!id) return state
    // Avoid mutating the existing map — return a new one so React sees the change.
    const next = new Map(state)
    next.set(id, {
      id,
      platinumData: d.platinumData,
      title: (d.title as string) || '',
      customer: d.customer as string | undefined,
      job: d.job as string | undefined,
      spec: d.spec as string | undefined,
    })
    return next
  },

  render({ state, sessionId }: { state: TablePluginState; toolCallId: string; sessionId: string }) {
    if (state.size === 0) return null
    const tables = Array.from(state.values())
    return (
      <>
        {tables.map(table => (
          <InlinePlatinumTable
            key={table.id}
            platinumData={table.platinumData}
            title={table.title || undefined}
            customer={table.customer}
            job={table.job}
            spec={table.spec}
            sessionId={sessionId}
            notebookCellId={table.id}
          />
        ))}
      </>
    )
  },
}
